import React, { useEffect, useCallback } from 'react';
import './HeadphoneTypeSelector.css';
import { useLanguage } from '../../../contexts/LanguageContext';

const SKELETON_TILE_COUNT = 4;

const HeadphoneTypeSelector = ({ recommendedTypes = [], isLoading = false, onClose }) => {
  const { translate } = useLanguage();

  // Convert type names to image paths
  const getImagePath = (typeName) => {
    // Convert "Over-Ear" or "Open-Ear" to "over-ear.png" or "open-ear.png"
    // Handle both space and hyphen variations
    const imageName = typeName.toLowerCase().replace(/[\s-]+/g, '-');
    console.log(`🖼️ Loading image for type: ${typeName} -> ${imageName}.png`);
    return `/Assets/SupportIcons/${imageName}.png`;
  };

  // Auto-close after 20 seconds (only once real data is shown — don't close while loading)
  useEffect(() => {
    if (isLoading) return;

    const timer = setTimeout(() => {
      onClose();
    }, 20000); // 20 seconds

    return () => {
      clearTimeout(timer);
    };
  }, [onClose, isLoading]);

  // Escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      const overlay = document.querySelector('.headphone-type-overlay');
      const gotokiButton = document.querySelector('gotoki-button');

      // Don't close if clicking the gotoki-button
      if (gotokiButton && gotokiButton.contains(e.target)) {
        return;
      }

      if (overlay && !overlay.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  const handleCloseClick = useCallback((e) => {
    e.stopPropagation();
    onClose();
  }, [onClose]);

  if (!isLoading && (!recommendedTypes || recommendedTypes.length === 0)) {
    return null;
  }

  // Determine grid columns based on number of types (or skeleton count while loading)
  const tileCount = isLoading ? SKELETON_TILE_COUNT : recommendedTypes.length;
  const getGridCols = () => {
    switch (tileCount) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-2';
      case 3:
        return 'grid-cols-3 max-md:grid-cols-2';
      case 4:
        return 'grid-cols-2';
      default:
        return 'grid-cols-2 md:grid-cols-3';
    }
  };

  return (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 flex flex-col items-center z-[100000] pointer-events-none w-auto max-w-[95%] sm:max-w-[90%] md:max-w-[500px] lg:max-w-[550px] xl:max-w-[600px] px-1 sm:px-2 md:px-[10px] lg:px-[15px] xl:px-5 pb-2 sm:pb-2 md:pb-[10px] lg:pb-3 xl:pb-[15px] mb-4 max-h-[calc(100vh-200px)]"
      onClick={(e) => e.stopPropagation()}
      style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'visible' }}
    >
      {/* Header with glassmorphism - simplified without call controls */}
      <div className="detail-header">
        <div className="detail-header-left">
          <span className="detail-live-dot"></span>
          <span className="detail-header-title">{translate('aiRecommendations')}</span>
        </div>
        <div className="detail-header-actions">
          <button className="detail-close-x" onClick={handleCloseClick} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Content Panel with glassmorphism */}
      <div className="headphone-type-panel">
        <div className="flex flex-col items-center justify-center gap-4 py-6 px-8 text-center max-md:py-5 max-md:px-6 max-sm:py-[15px] max-sm:px-5">
          <h2 className="font-mont text-2xl font-extrabold text-white m-0 text-center max-md:text-xl max-sm:text-base">
            {translate('selectHeadphoneType') || 'Select Your Headphone Type'}
          </h2>
          <p className="font-mont text-[0.95rem] text-white/75 m-0 text-center max-md:text-[0.85rem] max-sm:text-xs">
            {translate('speakYourChoice') || 'Please speak your preferred type'}
          </p>

          {/* Grid of headphone type images (or skeleton placeholders while loading) */}
          <div className={`grid ${getGridCols()} gap-4 w-full max-w-[550px] mt-2.5 max-md:gap-[15px] max-sm:gap-3`}>
            {isLoading
              ? Array.from({ length: SKELETON_TILE_COUNT }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="flex flex-col items-center gap-2">
                    <div className="w-full aspect-square border-2 border-white/10 rounded-xl skeleton-shimmer" />
                    <span
                      className="font-mont text-[0.95rem] font-bold text-center uppercase tracking-[0.5px] max-md:text-[0.85rem] max-sm:text-xs w-2/3 rounded text-transparent skeleton-shimmer"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </span>
                  </div>
                ))
              : recommendedTypes.map((type, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center gap-2 cursor-pointer group"
                    onClick={() => onClose()}
                  >
                    {/* Image box - full width */}
                    <div className="w-full aspect-square bg-white/5 border-2 border-brand-orange/30 rounded-xl transition-all duration-300 hover:bg-brand-orange/10 hover:border-brand-orange/60 hover:-translate-y-1 hover:shadow-[0_8px_20px_rgba(255,106,42,0.3)] overflow-hidden">
                      <img
                        src={getImagePath(type)}
                        alt={type}
                        className="w-full h-full object-cover [filter:drop-shadow(0_4px_12px_rgba(0,0,0,0.3))] transition-all duration-300 group-hover:[filter:drop-shadow(0_8px_20px_rgba(255,106,42,0.4))] group-hover:scale-105"
                        onError={(e) => {
                          console.error(`Failed to load image for ${type}`);
                          e.target.src = '/Assets/placeholder.png'; // Fallback image
                        }}
                      />
                    </div>
                    {/* Text beneath the box */}
                    <span className="font-mont text-[0.95rem] font-bold text-white text-center uppercase tracking-[0.5px] max-md:text-[0.85rem] max-sm:text-xs">
                      {type}
                    </span>
                  </div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeadphoneTypeSelector;

